import React, { useContext, useState } from 'react';
import moment from 'moment-timezone';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { parse, stringify } from '../../utils/UrlUtil';
import { useLocation, useHistory } from 'react-router-dom';
import { AccountContext } from '../../components/AccountContext';
import useSubscriptionExpiredRestrictions from '../../hooks/useSubscriptionExpiredRestrictions';
import { RespondentType } from '../../shared/Constants';

const messages = defineMessages({
  all: { id: 'lbl.respondent_all', defaultMessage: 'All' },
  internal: { id: 'respondent.internal', defaultMessage: 'Internal respondents' },
  external: { id: 'respondent.external', defaultMessage: 'External respondents' },
  startDate: { id: 'placeholder.startWith', defaultMessage: 'Start date' },
  endDate: { id: 'placeholder.endWith', defaultMessage: 'End date' },
  addRespondent: { id: 'candidate.add', defaultMessage: 'Add Respondent' },
});

export const Employee = () => <i className="fas fa-user-circle" aria-hidden="true" />;
export const NonEmployee = () => <i className="fas fa-user" aria-hidden="true" />;

const employeeFilterIconTemplate = (option) => {
  switch (option.value) {
    case RespondentType.INTERNAL:
      return (
        <>
          <Employee /> {option.label}
        </>
      );
    case RespondentType.EXTERNAL:
      return (
        <>
          <NonEmployee /> {option.label}
        </>
      );
    default:
      return option.label;
  }
};

const RespondentsDataTableHeader = () => {
  const { subscriptionIsExpired } = useSubscriptionExpiredRestrictions();

  const history = useHistory();
  const { formatMessage } = useIntl();
  const currentAccount = useContext(AccountContext);
  const location = useLocation();
  const params = parse(location.search);
  const [filter, setFilter] = useState({
    query: '',
    respondentType: 'ALL',
    from: undefined,
    to: undefined,
    ...params.filter,
  });

  /**
   * Get old URL and:
   * - parse query params
   * - merge new param values
   * - stringify query params
   * - push new url
   */
  const onQueryVariableChange = (filter) => {
    const params = parse(location.search);

    // build the filter
    const newFilter = {
      ...params.filter,
      ...filter,
    };

    // handle edge case for respondentType = ALL.
    if (newFilter.respondentType === 'ALL') {
      newFilter.respondentType = undefined;
    }

    // handle date filter
    if (newFilter.from) {
      const currentTimezone = moment.tz.guess() || 'UTC';
      const fromDate = newFilter.from ? moment.tz(newFilter.from, currentTimezone).format('YYYY-MM-DD') : null;
      newFilter.from = fromDate;
    }

    if (newFilter.to) {
      // make sure the time is parsing with timezone

      const currentTimezone = moment.tz.guess() || 'UTC';
      const toDate = newFilter.to ? moment.tz(newFilter.to, currentTimezone).format('YYYY-MM-DD') : null;
      newFilter.to = toDate;
    }

    const search = stringify({
      ...params,
      filter: newFilter,
    });

    // update url with the new search, keep the rest of the values
    history.push({
      ...location,
      search,
    });
  };

  /**
   * Update the filter Query.
   */
  const onChangeQueryText = (event) => {
    const updatedFilter = {
      ...filter,
      query: event.currentTarget.value,
    };

    setFilter(updatedFilter);
  };

  const onRespondentTypeChange = (event) => {
    const updatedFilter = {
      ...filter,
      respondentType: event.value,
    };

    setFilter(updatedFilter);

    onQueryVariableChange(updatedFilter);
  };

  const onDateFilterChange = (params, dateFilter) => {
    const updatedFilter = {
      ...filter,
      [dateFilter]: params.value,
    };

    setFilter(updatedFilter);

    onQueryVariableChange(updatedFilter);
  };

  const buildTypeOptions = () => {
    const options = [
      { label: formatMessage(messages.all), value: 'ALL' },
      { label: formatMessage(messages.internal), value: RespondentType.INTERNAL },
      { label: formatMessage(messages.external), value: RespondentType.EXTERNAL },
    ];
    return options;
  };

  const navigateToCreation = () => {
    history.push(`/accounts/${currentAccount}/respondents/new`);
  };

  const start = formatMessage(messages.startDate);
  const end = formatMessage(messages.endDate);

  const fromFilter = filter.from ? new Date(filter.from) : null;
  const toFilter = filter.to ? new Date(filter.to) : null;

  const addRespondentLabel = formatMessage(messages.addRespondent);
  return (
    <>
      <div className="p-grid">
        <div className="p-col-12">
          <div className="pull-right">
            <Button icon="pi pi-plus" disabled={subscriptionIsExpired} label={addRespondentLabel} onClick={navigateToCreation} />
          </div>
        </div>
      </div>
      <div className="p-grid p-fluid">
        <div className="p-col-12 p-sm-6 p-md-3">
          <div className="p-inputgroup">
            <InputText
              value={filter.query}
              onChange={onChangeQueryText}
              onKeyPress={(event) => {
                if (event.key === 'Enter') {
                  onQueryVariableChange(filter);
                }
              }}
            />
            <Button
              icon="pi pi-search"
              // For search we have a search button.
              // The state is already updated. We just upate the url
              onClick={() => onQueryVariableChange(filter)}
            />
          </div>
        </div>
        <div className="p-col-12 p-sm-6 p-md-3">
          <Calendar
            // TODO: load locale data from CLDR - see ca-gregorian.json // locale={locale}
            value={fromFilter}
            onChange={(params) => {
              onDateFilterChange(params, 'from');
            }}
            readOnlyInput
            showIcon
            showButtonBar
            dateFormat="yy-M-dd"
            placeholder={start}
          />
        </div>
        <div className="p-col-12 p-sm-6 p-md-3">
          <Calendar
            value={toFilter}
            onChange={(params) => {
              onDateFilterChange(params, 'to');
            }}
            dateFormat="yy-M-dd"
            readOnlyInput
            showIcon
            showButtonBar
            placeholder={end}
          />
        </div>

        <div className="p-col-12 p-sm-6 p-md-3">
          <FormattedMessage id="attrs.candidateType" defaultMessage="Respondent type">
            {(text) => (
              <Dropdown
                value={filter.respondentType}
                options={buildTypeOptions()}
                itemTemplate={employeeFilterIconTemplate}
                onChange={onRespondentTypeChange}
                tooltip={text}
                tooltipOptions={{ position: 'top', fontSize: '10px' }}
              />
            )}
          </FormattedMessage>
        </div>
      </div>
    </>
  );
};

export default RespondentsDataTableHeader;
